"use client";

import React from "react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const wp_query_params = {
  per_page: 8,
  post_type: "product",
  status: "publish"
};
const wp_query = new WP_Query(wp_query_params);
function calculateDiscount(price: number, discountedPrice: number) {
  if (!price || !discountedPrice || price <= discountedPrice) return 0;
  return Math.round((price - discountedPrice) / price * 100);
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    loadMore,
    isRefetching
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <Skeleton className="aspect-[4/5] w-full rounded-2xl" />
            <div className="space-y-2 px-2 text-center">
              <Skeleton className="mx-auto h-5 w-3/4" />
              <Skeleton className="mx-auto h-5 w-1/2" />
              <Skeleton className="mx-auto mt-4 h-10 w-full rounded-full" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">
          A apărut o eroare la încărcarea produselor.
        </p>
        <Button onClick={refetch} variant="outline">
          Reîncearcă
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center py-24 text-center">
        <h3 className="font-default text-2xl font-semibold text-foreground">
          Nu am găsit produse
        </h3>
        <p className="mt-2 text-muted-foreground">
          Momentan nu există produse disponibile în această categorie.
        </p>
      </div>;
  }
  return <div className="flex flex-col items-center space-y-12">
      <div className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((product, i) => {
        const isBracelet = product.categories?.some(cat => ["Brățări Individuale", "Seturi 3 Brățări Unisex", "Set 5 Bratari Unisex"].includes(cat.name));
        return <Card key={product.id} data-index={i} className="group border-none bg-card shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
              <Link to={product.link} className="relative block overflow-hidden rounded-t-xl">
                {isBracelet && <span className="absolute top-2 left-2 z-10 rounded-sm bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-xs">
                    Placat cu Aur 18K
                  </span>}
                <AspectRatio ratio={4 / 5} className="bg-muted">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-accent/50 text-muted-foreground">
                      Fără imagine
                    </div>}
                </AspectRatio>
              </Link>
              <CardContent className="flex flex-col items-center p-6 text-center">
                <Link to={product.link} className="mb-2 block w-full">
                  <h3 className="font-default text-lg font-semibold text-foreground line-clamp-2 hover:text-primary transition-colors">
                    {product.title}
                  </h3>
                </Link>

                {product.discounted_price && product.price && <div className="mb-2">
                    <span className="inline-flex items-center rounded-full bg-destructive px-2.5 py-0.5 text-xs font-semibold text-destructive-foreground">
                      -{calculateDiscount(product.price, product.discounted_price)}%
                    </span>
                  </div>}

                <div className="mb-6 flex items-center justify-center gap-3">
                  {product.discounted_price ? <>
                      <span className="font-default text-2xl font-bold text-primary">
                        {product.discounted_price} Lei
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        {product.price} Lei
                      </span>
                    </> : <span className="font-default text-xl font-bold text-primary">
                      {product.price !== undefined ? `${product.price} Lei` : ""}
                    </span>}
                </div>

                <div className="mt-auto w-full">
                  <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all" text="Adaugă în coș" showQuantity={false} />
                </div>
              </CardContent>
            </Card>;
      })}
      </div>

      {hasNext && <Button onClick={async () => await loadMore()} disabled={isRefetching} variant="outline" size="lg" className="min-w-[200px] rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          {isRefetching ? "Se încarcă..." : "Încarcă mai multe"}
        </Button>}
    </div>;
}
export default function ProductListingGrid() {
  return <section id="productlistinggrid" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Colecția Noastră
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground md:text-lg">
            Descoperă bijuterii elegante la prețuri imbatabile. Alege calitatea premium și designul sofisticat.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        post_type: "product",
        status: ["publish"]
      }}>
          <ProductsGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}